import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";

import { getManagementDashboardActionHistory } from "../../../../../../infrastructure/api/managementDashboard";
import { formatNumber } from "../../../../../../infrastructure/utils/formatters";
import ActionHistoryTimeline from "./ActionHistoryTimeline";
import { ManagementWidgetShell, severityChipColor, severityLabel } from "../managementDashboardWidgets";
import { buildDrillDownHref, isApiSuccess, pick } from "../managementDashboardUtils";

const STATUS_LABELS = {
  1: "باز",
  2: "در حال انجام",
  3: "تکمیل",
  4: "لغو",
  Open: "باز",
  InProgress: "در حال انجام",
  Completed: "تکمیل",
  Cancelled: "لغو",
};

const ManagementActionCenter = ({
  loading,
  error,
  suggestionsData,
  workflowData,
  workflowLoading,
  dashboardFilters,
  dashboardAccess,
  onRetry,
  onSync,
  onUpdateAction,
  syncing,
}) => {
  const [tab, setTab] = useState(0);
  const [assignDialog, setAssignDialog] = useState(null);
  const [assignUserId, setAssignUserId] = useState("");
  const [assignUserName, setAssignUserName] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyActionId, setHistoryActionId] = useState(null);

  const canAssign = dashboardAccess?.canAssignActions !== false;
  const canComplete = dashboardAccess?.canCompleteActions !== false;
  const canSync = dashboardAccess?.canSyncActions !== false;

  const suggestions = suggestionsData?.items || suggestionsData?.Items || [];
  const workflowItems = workflowData?.items || workflowData?.Items || [];
  const openCount = pick(workflowData, "openCount", "OpenCount") ?? 0;
  const completedCount = pick(workflowData, "completedCount", "CompletedCount") ?? 0;

  const filteredWorkflow = useMemo(() => {
    if (tab === 1) {
      return workflowItems.filter(
        (a) => pick(a, "status", "Status") === 3 || pick(a, "status", "Status") === "Completed"
      );
    }
    if (tab === 0) {
      return workflowItems.filter((a) => {
        const s = pick(a, "status", "Status");
        return s === 1 || s === 2 || s === "Open" || s === "InProgress";
      });
    }
    return workflowItems;
  }, [workflowItems, tab]);

  const loadHistory = useCallback(async (actionId) => {
    try {
      setHistoryActionId(actionId);
      setHistoryLoading(true);
      setHistoryError(null);
      const res = await getManagementDashboardActionHistory(actionId);
      if (isApiSuccess(res)) setHistoryData(res.data);
      else {
        setHistoryData(null);
        setHistoryError(res?.message || "دریافت تاریخچه ناموفق بود.");
      }
    } catch {
      setHistoryData(null);
      setHistoryError("خطا در دریافت تاریخچه اقدام.");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleAssign = useCallback(async () => {
    if (!assignDialog || !onUpdateAction || !canAssign) return;
    await onUpdateAction({
      id: assignDialog.id,
      assignedUserId: assignUserId ? Number(assignUserId) : null,
      assignedUserName: assignUserName || undefined,
    });
    setAssignDialog(null);
    setAssignUserId("");
    setAssignUserName("");
  }, [assignDialog, assignUserId, assignUserName, onUpdateAction, canAssign]);

  const handleStatus = useCallback(
    async (action, status) => {
      if (!onUpdateAction || !canComplete) return;
      await onUpdateAction({ id: pick(action, "id", "Id"), status });
    },
    [onUpdateAction, canComplete]
  );

  return (
    <ManagementWidgetShell
      title="مرکز اقدام"
      subheader="گردش کار اقدامات + پیشنهادهای هوش عملیاتی"
      loading={loading && workflowLoading}
      error={error}
      onRetry={onRetry}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
        <Chip label={`باز: ${formatNumber(openCount)}`} color="warning" size="small" />
        <Chip label={`تکمیل: ${formatNumber(completedCount)}`} color="success" size="small" variant="outlined" />
        {canSync ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<SyncOutlinedIcon />}
            onClick={() => onSync?.(dashboardFilters)}
            disabled={syncing}
          >
            همگام‌سازی از هوش عملیاتی
          </Button>
        ) : null}
      </Stack>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 1.5, minHeight: 40 }}>
        <Tab label="اقدامات باز" sx={{ minHeight: 40, fontSize: 13 }} />
        <Tab label="تکمیل‌شده" sx={{ minHeight: 40, fontSize: 13 }} />
        <Tab label="پیشنهادها" sx={{ minHeight: 40, fontSize: 13 }} />
      </Tabs>

      {tab < 2 ? (
        <Stack spacing={1.5}>
          {filteredWorkflow.map((action) => {
            const id = pick(action, "id", "Id");
            const status = pick(action, "status", "Status");
            const href = buildDrillDownHref(pick(action, "drillDown", "DrillDown"));
            const isOpen = status === 1 || status === 2 || status === "Open" || status === "InProgress";

            return (
              <Box
                key={id}
                sx={{ p: 1.5, borderRadius: 2, border: "1px solid", borderColor: "divider" }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                  <Chip
                    size="small"
                    color={severityChipColor(pick(action, "priority", "Priority"))}
                    label={severityLabel(pick(action, "priority", "Priority"))}
                  />
                  <Typography fontWeight={700}>{pick(action, "title", "Title")}</Typography>
                  <Chip size="small" variant="outlined" label={STATUS_LABELS[status] || status} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {pick(action, "description", "Description")}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                  {pick(action, "assignedUserName", "AssignedUserName") ? (
                    <Chip size="small" label={`مسئول: ${pick(action, "assignedUserName", "AssignedUserName")}`} />
                  ) : (
                    <Chip size="small" label="بدون مسئول" variant="outlined" />
                  )}
                  {pick(action, "dueDate", "DueDate") ? (
                    <Chip
                      size="small"
                      label={`مهلت: ${new Date(pick(action, "dueDate", "DueDate")).toLocaleDateString("fa-IR")}`}
                    />
                  ) : null}
                  {href ? (
                    <Button component={RouterLink} to={href} size="small">
                      مشاهده
                    </Button>
                  ) : null}
                  <Button size="small" onClick={() => loadHistory(id)}>
                    تاریخچه
                  </Button>
                  {isOpen ? (
                    <>
                      {canAssign ? (
                        <Button size="small" variant="outlined" onClick={() => setAssignDialog({ id })}>
                          تخصیص
                        </Button>
                      ) : null}
                      {canComplete ? (
                        <>
                          <Button size="small" variant="outlined" onClick={() => handleStatus(action, 2)}>
                            در حال انجام
                          </Button>
                          <Button
                            size="small"
                            color="success"
                            variant="outlined"
                            onClick={() => handleStatus(action, 3)}
                          >
                            تکمیل
                          </Button>
                        </>
                      ) : null}
                    </>
                  ) : null}
                </Stack>
              </Box>
            );
          })}
          {!filteredWorkflow.length && !workflowLoading ? (
            <Typography color="text.secondary">
              {tab === 0 ? "اقدام بازی ثبت نشده — «همگام‌سازی» را بزنید." : "اقدام تکمیل‌شده‌ای نیست."}
            </Typography>
          ) : null}
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          {suggestions.map((item) => {
            const code = pick(item, "code", "Code");
            const actions = item.actions || item.Actions || [];
            return (
              <Box key={code} sx={{ p: 1.5, borderRadius: 2, border: "1px dashed", borderColor: "divider" }}>
                <Typography fontWeight={700}>{pick(item, "title", "Title")}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {pick(item, "description", "Description")}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                  {actions.slice(0, 2).map((a) => {
                    const href = buildDrillDownHref(pick(a, "drillDown", "DrillDown"));
                    if (!href) return null;
                    return (
                      <Button
                        key={pick(a, "actionType", "ActionType")}
                        component={RouterLink}
                        to={href}
                        size="small"
                      >
                        {pick(a, "label", "Label")}
                      </Button>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}
          {!suggestions.length ? (
            <Typography color="text.secondary">پیشنهاد جدیدی وجود ندارد.</Typography>
          ) : null}
        </Stack>
      )}

      {historyActionId || historyLoading || historyError ? (
        <Box sx={{ mt: 2 }}>
          <ActionHistoryTimeline
            loading={historyLoading}
            error={historyError}
            data={historyData}
            onRetry={() => historyActionId && loadHistory(historyActionId)}
          />
        </Box>
      ) : null}

      <Dialog open={Boolean(assignDialog)} onClose={() => setAssignDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>تخصیص مسئول</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField
              label="شناسه کاربر"
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
              size="small"
              fullWidth
              type="number"
            />
            <TextField
              label="نام مسئول"
              value={assignUserName}
              onChange={(e) => setAssignUserName(e.target.value)}
              size="small"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(null)}>انصراف</Button>
          <Button variant="contained" onClick={handleAssign}>
            ذخیره
          </Button>
        </DialogActions>
      </Dialog>
    </ManagementWidgetShell>
  );
};

export default ManagementActionCenter;
